import { useState } from 'react';
import * as _ from 'lodash';
import type { User } from '../../../../types';
import { put } from '../../../../redux/api';
import { useAddToast } from '../../../../contexts/ToastContext';
import { errorMessagesListFromResponse, type FailedFetchResponse } from '../../../../helpers/errors';

type UserProfile = Pick<User, 'id' | 'username' | 'email' | 'sso_only'> & {
  currentPassword?: string;
  newPassword?: string;
  newPasswordConfirm?: string;
};

/**
 * Hook for submitting the user profile.
 */
const useUserProfile = (user?: User) => {
  const addToast = useAddToast();
  const [userProfile, setUserProfile] = useState<UserProfile>({
    id: user?.id ?? 0,
    email: user?.email ?? '',
    username: user?.username ?? '',
    sso_only: user?.sso_only ?? false,
    currentPassword: '',
    newPassword: '',
    newPasswordConfirm: '',
  });
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const onUpdateUserProfile = (newUserProfile: Partial<UserProfile>) => {
    if (!('currentPassword' in newUserProfile)) {
      setIsDirty(true);
    }
    setUserProfile({
      ...userProfile,
      ...newUserProfile,
    });
  };

  const onSave = async () => {
    try {
      const newPasswordLength = userProfile?.newPassword?.length || 0;
      if (!userProfile.currentPassword) {
        addToast({ type: 'error', content: 'Please enter your current password to make changes' });
        return;
      }
      if (newPasswordLength > 0 && userProfile.newPassword !== userProfile.newPasswordConfirm) {
        addToast({ type: 'error', content: 'Passwords do not match.' });
        return;
      }

      await put(`/api/users/${userProfile.id}`, {
        ...(user || {}), // Note, pretty sure we don't technically need this, but leaving as-is for parity with v1
        // The newPassword and newPasswordConfirm fields are for validation only. The API does not accept them.
        ..._.omit(userProfile, ['newPassword', 'newPasswordConfirm']),
        // Instead, the API accepts a password field.
        // Set password to undefined if the user is not changing their password.
        password: userProfile.newPassword || undefined,
      });
      addToast({ type: 'success', content: 'Saved login settings.' });
      setIsDirty(false);
    } catch (err: unknown) {
      const errors = errorMessagesListFromResponse(err as FailedFetchResponse);
      addToast({ type: 'error', content: errors[0]?.message || 'Failed to save login settings.' });
    } finally {
      setIsSaving(false);
    }
  };

  const hasEnteredCurrentPassword = !!userProfile.currentPassword;
  return {
    userProfile,
    isSsoOnly: userProfile.sso_only,
    hasEnteredCurrentPassword,
    onUpdateUserProfile,
    onSave,
    isSaveButtonDisabled: !isDirty || isSaving || !hasEnteredCurrentPassword,
    isSaving,
  };
};

export default useUserProfile;
